import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ComposedChart, ScatterChart, Scatter, Treemap
} from "recharts";
import { fetchCaptures } from "../api/captureApi";
import "../styles/PackhouseDashboard.css";

// ─────────────────────────────────────────────
// BRAND CONSTANTS
// ─────────────────────────────────────────────
const BRAND = {
  pink: "#a92f6f",
  pinkLight: "#d44a8f",
  pinkDark: "#7a1f50",
  olive: "#a9ae50",
  oliveLight: "#c4c970",
  oliveDark: "#7a7e3a",
  bg: "#fafaf8",
  surface: "#ffffff",
  text: "#1a1a1a",
  muted: "#6b6b6b",
  border: "#e8e6e1",
};

const PALETTE = [
  "#a92f6f", "#a9ae50", "#4b8ca8", "#c8a84b",
  "#6fa84b", "#a84b6f", "#4b7ca8", "#a87c4b",
  "#8b5a8c", "#5a8c7a", "#c47a5a", "#5a6ac4"
];

// ─────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) : "0.0");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
const calcStems = (c) => (parseInt(c.bunch_count) || 0) * (parseInt(c.bunch_size_value) || 0);
const farmName = (c) => c.farm_name || c.farm || "";

function downloadCSV(data, filename) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(","), ...data.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────
// DATE UTILS — locked to last 7 days
// ─────────────────────────────────────────────
function getLast7Range() {
  const today = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const from = new Date(today);
  from.setDate(today.getDate() - 7);
  return { dateFrom: iso(from), dateTo: iso(today) };
}

const DATE_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Yesterday", yesterday: true },
  { label: "Last 3 Days", days: 3 },
  { label: "Last 7 Days", days: 7 },
];

function getPresetRange(preset) {
  const today = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const maxDate = iso(today);
  const minDate = (() => { const d = new Date(today); d.setDate(d.getDate() - 7); return iso(d); })();

  if (preset.yesterday) {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    const yIso = iso(y);
    return { dateFrom: yIso < minDate ? minDate : yIso, dateTo: yIso < minDate ? minDate : yIso };
  }
  const from = new Date(today); from.setDate(today.getDate() - preset.days);
  const fromIso = iso(from);
  return { dateFrom: fromIso < minDate ? minDate : fromIso, dateTo: maxDate };
}

// ─────────────────────────────────────────────
// SPARKLINE
// ─────────────────────────────────────────────
const Sparkline = ({ data, dataKey, color = BRAND.pink, height = 40 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data}>
      <defs><linearGradient id={`spk-${dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.3}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
      <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#spk-${dataKey})`} strokeWidth={2} dot={false} />
    </AreaChart>
  </ResponsiveContainer>
);

// ─────────────────────────────────────────────
// HEATMAP
// ─────────────────────────────────────────────
const Heatmap = ({ data }) => {
  const hours = Array.from({ length: 12 }, (_, i) => `${i * 2}:00`);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const map = useMemo(() => {
    const m = {}; days.forEach(d => { m[d] = {}; hours.forEach(h => { m[d][h] = 0; }); });
    data.forEach(c => {
      const d = new Date(c.captured_at);
      const day = days[d.getDay()];
      const hour = `${Math.floor(d.getHours() / 2) * 2}:00`;
      m[day][hour] += calcStems(c);
    });
    return m;
  }, [data]);
  const maxVal = useMemo(() => { let mx = 0; days.forEach(d => hours.forEach(h => mx = Math.max(mx, map[d][h]))); return mx || 1; }, [map]);
  const intensity = (v) => { const r = v / maxVal; if (r === 0) return "#f3f4f6"; if (r < 0.2) return "#fce7f3"; if (r < 0.4) return "#f9a8d4"; if (r < 0.6) return "#f472b6"; if (r < 0.8) return "#d44a8f"; return "#a92f6f"; };
  return (
    <div className="ph-heatmap">
      <div className="ph-heatmap-title">⏰ Activity Heatmap — Stems by Day & Hour</div>
      <div className="ph-heatmap-grid">
        <div className="ph-heatmap-days">{days.map(d => <div key={d} className="ph-heatmap-day">{d}</div>)}</div>
        <div className="ph-heatmap-cells">
          {hours.map(h => (
            <div key={h} className="ph-heatmap-col">
              <div className="ph-heatmap-hlabel">{h}</div>
              {days.map(d => <div key={`${d}-${h}`} className="ph-heatmap-cell" style={{ background: intensity(map[d][h]) }} title={`${d} ${h}: ${fmt(map[d][h])} stems`} />)}
            </div>
          ))}
        </div>
      </div>
      <div className="ph-heatmap-legend"><span>Low</span>{["#f3f4f6","#fce7f3","#f9a8d4","#f472b6","#d44a8f","#a92f6f"].map(c => <div key={c} style={{ background: c }} />)}<span>High</span></div>
    </div>
  );
};

// ─────────────────────────────────────────────
// PODIUM
// ─────────────────────────────────────────────
const Podium = ({ data, labelKey = "name", valueKey = "stems", unit = "stems" }) => {
  const top3 = data.slice(0, 3);
  const order = [1, 0, 2];
  const heights = [90, 130, 70];
  const medals = ["🥈", "🥇", "🥉"];
  const labels = ["2nd", "1st", "3rd"];
  const colors = ["#9ca3af", BRAND.olive, "#92400e"];
  return (
    <div className="ph-podium">
      {order.map((idx, pos) => {
        const p = top3[idx]; if (!p) return null;
        return (
          <div key={p[labelKey]} className="ph-podium-slot" style={{ "--h": `${heights[pos]}px`, "--c": colors[pos] }}>
            <div className="ph-podium-info">
              <div className="ph-podium-medal">{medals[pos]}</div>
              <div className="ph-podium-name">{p[labelKey]}</div>
              <div className="ph-podium-val">{fmt(p[valueKey])}</div>
              <div className="ph-podium-sub">{unit}</div>
            </div>
            <div className="ph-podium-block"><span>{labels[pos]}</span></div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────
const StatCard = ({ label, value, icon, accent, sub, trend, sparkData, sparkKey }) => (
  <div className="ph-stat-card" style={{ "--accent": accent }}>
    <div className="ph-stat-top">
      <div className="ph-stat-icon">{icon}</div>
      {trend !== undefined && <div className={`ph-trend ${trend >= 0 ? "up" : "down"}`}>{trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%</div>}
    </div>
    <div className="ph-stat-val">{value}</div>
    <div className="ph-stat-label">{label}</div>
    {sub && <div className="ph-stat-sub">{sub}</div>}
    {sparkData && sparkKey && <div className="ph-stat-spark"><Sparkline data={sparkData} dataKey={sparkKey} color={accent} /></div>}
  </div>
);

// ─────────────────────────────────────────────
// MINI CHIP
// ─────────────────────────────────────────────
const MiniChip = ({ label, active, onClick }) => (
  <button className={`ph-chip ${active ? "ph-chip-active" : ""}`} onClick={onClick}>{label}</button>
);

// ─────────────────────────────────────────────
// DATE SLICER — locked to last 7 days window
// ─────────────────────────────────────────────
const DateSlicer = ({ dateFrom, dateTo, onChange }) => {
  const [open, setOpen] = useState(false);

  const today = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const maxDate = iso(today);
  const minDate = (() => { const d = new Date(today); d.setDate(d.getDate() - 7); return iso(d); })();

  const activePreset = DATE_PRESETS.find((p) => {
    const r = getPresetRange(p);
    return r.dateFrom === dateFrom && r.dateTo === dateTo;
  });

  const handleDateChange = (key, val) => {
    let clamped = val;
    if (clamped < minDate) clamped = minDate;
    if (clamped > maxDate) clamped = maxDate;
    onChange({ dateFrom: key === "dateFrom" ? clamped : dateFrom, dateTo: key === "dateTo" ? clamped : dateTo });
  };

  return (
    <div className="ph-date-slicer">
      <div className="ph-date-trigger" onClick={() => setOpen(v => !v)}>
        <span className="ph-date-icon">📅</span>
        <div className="ph-date-display">
          <span className="ph-date-preset-label">{activePreset?.label || "Custom Range"}</span>
          <span className="ph-date-range-text">{dateFrom || dateTo ? `${dateFrom || "Start"} → ${dateTo || "End"}` : "Last 7 days data"}</span>
        </div>
        <span className={`ph-date-arrow ${open ? "open" : ""}`}>▾</span>
      </div>
      {open && (
        <div className="ph-date-panel">
          <div className="ph-date-window-note">📌 Data available for the last 7 days only</div>
          <div className="ph-date-presets">
            {DATE_PRESETS.map(p => (
              <button key={p.label} className={`ph-preset-btn ${activePreset?.label === p.label ? "active" : ""}`}
                onClick={() => { onChange(getPresetRange(p)); setOpen(false); }}>{p.label}</button>
            ))}
          </div>
          <div className="ph-date-divider">— or pick custom —</div>
          <div className="ph-date-inputs">
            <div className="ph-date-field">
              <label>From</label>
              <input type="date" value={dateFrom} min={minDate} max={dateTo || maxDate}
                onChange={e => handleDateChange("dateFrom", e.target.value)} />
            </div>
            <div className="ph-date-field">
              <label>To</label>
              <input type="date" value={dateTo} min={dateFrom || minDate} max={maxDate}
                onChange={e => handleDateChange("dateTo", e.target.value)} />
            </div>
          </div>
          <div className="ph-date-window-range">Range: {minDate} → {maxDate}</div>
          <button className="ph-date-close" onClick={() => setOpen(false)}>Apply ✓</button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ph-tooltip">
      <div className="ph-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="ph-tooltip-row"><span className="ph-tooltip-dot" style={{ background: p.color }} /><span>{p.name}:</span><b>{fmt(p.value)}</b></div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const PackhouseDashboard = () => {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [captures, setCaptures] = useState([]);

  // Default filters: last 7 days
  const defaultRange = getLast7Range();
  const [filters, setFilters] = useState({
    dateFrom: defaultRange.dateFrom,
    dateTo: defaultRange.dateTo,
    farm: "", ghse: "", variety: "", length: "", worker: "", harvest_type: ""
  });

  const [sortCol, setSortCol] = useState("captured_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [animateStats, setAnimateStats] = useState(false);

  // 🔥 FETCH WITH DATE RANGE — passes filters to API
  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const params = {};
        if (filters.dateFrom) params.date_from = filters.dateFrom;
        if (filters.dateTo) params.date_to = filters.dateTo;

        const cap = await fetchCaptures(params).catch(() => []);
        const data = Array.isArray(cap) ? cap : cap?.results || [];
        data.sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at));
        setCaptures(data);
        setTimeout(() => setAnimateStats(true), 300);
      } catch (e) { setError("Failed to load packhouse data."); console.error(e); }
      finally { setLoading(false); }
    })();
  }, [filters.dateFrom, filters.dateTo]);

  const upd = useCallback((k, v) => setFilters(p => ({ ...p, [k]: v })), []);
  const resetFilters = () => {
    const range = getLast7Range();
    setFilters({ dateFrom: range.dateFrom, dateTo: range.dateTo, farm: "", ghse: "", variety: "", length: "", worker: "", harvest_type: "" });
  };

  // Cascading filters
  const farms = useMemo(() => [...new Set(captures.map(c => farmName(c)).filter(Boolean))].sort(), [captures]);
  const ghses = useMemo(() => { const src = filters.farm ? captures.filter(c => farmName(c) === filters.farm) : captures; return [...new Set(src.map(c => c.ghse_name).filter(Boolean))].sort(); }, [captures, filters.farm]);
  const varieties = useMemo(() => { let src = captures; if (filters.farm) src = src.filter(c => farmName(c) === filters.farm); if (filters.ghse) src = src.filter(c => c.ghse_name === filters.ghse); return [...new Set(src.map(c => c.variety_name).filter(Boolean))].sort(); }, [captures, filters.farm, filters.ghse]);
  const lengths = useMemo(() => [...new Set(captures.map(c => c.length_name).filter(Boolean))].sort(), [captures]);
  const workers = useMemo(() => [...new Set(captures.map(c => c.worker_name || c.worker).filter(Boolean))].sort(), [captures]);
  const harvestTypes = useMemo(() => [...new Set(captures.map(c => c.harvest_type_name).filter(Boolean))].sort(), [captures]);

  const handleFarmChange = (val) => setFilters(p => ({ ...p, farm: val, ghse: "", variety: "" }));
  const handleGhseChange = (val) => setFilters(p => ({ ...p, ghse: val, variety: "" }));

  // Filtered records (client-side filtering for non-date fields)
  const filtered = useMemo(() => captures.filter(c => {
    if (filters.farm && farmName(c) !== filters.farm) return false;
    if (filters.ghse && c.ghse_name !== filters.ghse) return false;
    if (filters.variety && c.variety_name !== filters.variety) return false;
    if (filters.length && c.length_name !== filters.length) return false;
    if (filters.worker && (c.worker_name || c.worker) !== filters.worker) return false;
    if (filters.harvest_type && c.harvest_type_name !== filters.harvest_type) return false;
    return true;
  }), [captures, filters]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // KPIs
  const totalStems = useMemo(() => filtered.reduce((s, c) => s + calcStems(c), 0), [filtered]);
  const totalBunches = useMemo(() => filtered.reduce((s, c) => s + (parseInt(c.bunch_count) || 0), 0), [filtered]);
  const uniqueWorkers = useMemo(() => new Set(filtered.map(c => c.worker_name || c.worker).filter(Boolean)).size, [filtered]);
  const avgBunchSize = useMemo(() => totalBunches ? (totalStems / totalBunches).toFixed(1) : "0.0", [totalStems, totalBunches]);

  // Time series
  const timeSeries = useMemo(() => {
    const map = {};
    filtered.forEach(c => { const d = c.captured_at ? new Date(c.captured_at).toISOString().split("T")[0] : null; if (!d) return; if (!map[d]) map[d] = { date: d, stems: 0, bunches: 0, captures: 0 }; map[d].stems += calcStems(c); map[d].bunches += parseInt(c.bunch_count) || 0; map[d].captures += 1; });
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filtered]);

  // Hourly productivity
  const hourlyData = useMemo(() => {
    const map = {};
    filtered.forEach(c => { const h = new Date(c.captured_at).getHours(); if (!map[h]) map[h] = { hour: `${h}:00`, stems: 0, bunches: 0, captures: 0 }; map[h].stems += calcStems(c); map[h].bunches += parseInt(c.bunch_count) || 0; map[h].captures += 1; });
    return Object.values(map).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  }, [filtered]);

  // Aggregations
  const agg = useCallback((keyFn) => {
    const map = {};
    filtered.forEach(c => {
      const k = keyFn(c) || "Unknown";
      if (!map[k]) map[k] = { name: k, stems: 0, bunches: 0, captures: 0, _workers: new Set(), _varieties: new Set() };
      map[k].stems += calcStems(c); map[k].bunches += parseInt(c.bunch_count) || 0; map[k].captures += 1;
      if (c.worker_name) map[k]._workers.add(c.worker_name); if (c.variety_name) map[k]._varieties.add(c.variety_name);
    });
    return Object.values(map).map(r => ({ ...r, workers: r._workers.size, varieties: r._varieties.size })).sort((a, b) => b.stems - a.stems);
  }, [filtered]);

  const byWorker = useMemo(() => agg(c => c.worker_name || c.worker || "Unknown"), [agg]);
  const byGHSE = useMemo(() => agg(c => c.ghse_name || `GHSE ${c.ghse_id}`), [agg]);
  const byVariety = useMemo(() => agg(c => c.variety_name || `V${c.variety}`), [agg]);
  const byLength = useMemo(() => agg(c => c.length_name || `L${c.length}`), [agg]);
  const byFarm = useMemo(() => agg(c => farmName(c) || "Unknown"), [agg]);
  const byHarvestType = useMemo(() => agg(c => c.harvest_type_name || "Unknown"), [agg]);

  // Variety pie data
  const varietyPie = useMemo(() => byVariety.map(v => ({ name: v.name, value: v.stems })), [byVariety]);

  // Treemap data
  const treemapData = useMemo(() => byVariety.map(v => ({ name: v.name, size: v.stems, stems: v.stems, bunches: v.bunches })), [byVariety]);

  // Length vs GHSE cross-tab
  const crossLGHSE = useMemo(() => {
    const cross = {};
    filtered.forEach(c => { const lk = c.length_name || `L${c.length}`; const gk = c.ghse_name || `GHSE${c.ghse_id}`; const key = `${lk}||${gk}`; if (!cross[key]) cross[key] = { length: lk, ghse: gk, stems: 0, bunches: 0 }; cross[key].stems += calcStems(c); cross[key].bunches += parseInt(c.bunch_count) || 0; });
    return Object.values(cross).sort((a, b) => b.stems - a.stems).slice(0, 40);
  }, [filtered]);

  // Trends
  const trends = useMemo(() => {
    const now = new Date();
    const last3 = filtered.filter(c => { const d = new Date(c.captured_at); return (now - d) <= 3 * 86400000; });
    const prev3 = filtered.filter(c => { const d = new Date(c.captured_at); return (now - d) > 3 * 86400000 && (now - d) <= 6 * 86400000; });
    const ls = last3.reduce((s, c) => s + calcStems(c), 0);
    const ps = prev3.reduce((s, c) => s + calcStems(c), 0);
    return { stems: ps ? ((ls - ps) / ps * 100) : 0 };
  }, [filtered]);

  // Table sorting & pagination
  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const resolve = (rec, col) => { if (col === "total_stems") return calcStems(rec); if (col === "farm_name") return farmName(rec); return rec[col]; };
      let av = resolve(a, sortCol), bv = resolve(b, sortCol);
      if (typeof av === "string") av = av.toLowerCase(); if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1; if (av > bv) return sortDir === "asc" ? 1 : -1; return 0;
    });
  }, [filtered, sortCol, sortDir]);
  const paged = sortedFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(sortedFiltered.length / PAGE_SIZE);

  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("desc"); } setPage(1); };

  const TABS = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "bunchers", label: "Bunchers", icon: "👷" },
    { id: "greenhouse", label: "Greenhouse", icon: "🏠" },
    { id: "lengths", label: "Lengths", icon: "📏" },
    { id: "varieties", label: "Varieties", icon: "🌿" },
    { id: "analytics", label: "Harvest Type", icon: "🔖" },
    { id: "table", label: "Raw Data", icon: "📋" },
  ];

  if (loading) return (
    <div className="ph-loading">
      <div className="ph-spinner-wrap"><div className="ph-spinner" /><div className="ph-spinner-ring" /></div>
      <p className="ph-loading-text">Loading Packhouse Analytics…</p>
      <div className="ph-loading-bar"><div className="ph-loading-progress" /></div>
    </div>
  );

  if (error) return (
    <div className="ph-error">
      <div className="ph-error-icon">⚠️</div>
      <p>{error}</p>
      <button onClick={() => window.location.reload()}>Retry</button>
    </div>
  );

  return (
    <div className="ph-root">

      {/* ── HEADER ── */}
      <header className="ph-header">
        <div className="ph-header-left">
          <div className="ph-logo-wrap"><div className="ph-logo-ring"><span>📦</span></div></div>
          <div className="ph-header-titles">
            <h1 className="ph-title">Packhouse Performance</h1>
            <p className="ph-subtitle">Bunchers · Stems · Lengths · Varieties · Greenhouses</p>
          </div>
        </div>
        <div className="ph-header-right">
          <div className="ph-live-badge"><span className="ph-live-dot" />{new Date().toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</div>
          <button className={`ph-toggle-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters(p => !p)}>
            ⚙ Filters{activeFilterCount > 0 && <span className="ph-badge">{activeFilterCount}</span>}
          </button>
          <button className="ph-export-btn" onClick={() => downloadCSV(filtered.map(c => ({ ...c, total_stems: calcStems(c) })), "packhouse_captures.csv")}>⬇ Export CSV</button>
        </div>
      </header>

      {/* ── FILTERS ── */}
      {showFilters && (
        <div className="ph-filters">
          <div className="ph-filters-inner">
            <div className="ph-filter-group ph-filter-date-group">
              <label className="ph-filter-label">📅 Date Range</label>
              <DateSlicer dateFrom={filters.dateFrom} dateTo={filters.dateTo} onChange={({ dateFrom, dateTo }) => setFilters(p => ({ ...p, dateFrom, dateTo }))} />
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">🚜 Farm</label>
              <div className="ph-select-wrap">
                <select value={filters.farm} onChange={e => handleFarmChange(e.target.value)}><option value="">All Farms</option>{farms.map(f => <option key={f} value={f}>{f}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">🏠 Greenhouse</label>
              <div className="ph-select-wrap">
                <select value={filters.ghse} onChange={e => handleGhseChange(e.target.value)}><option value="">All GHSEs</option>{ghses.map(g => <option key={g} value={g}>{g}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">🌿 Variety</label>
              <div className="ph-select-wrap">
                <select value={filters.variety} onChange={e => upd("variety", e.target.value)}><option value="">All Varieties</option>{varieties.map(v => <option key={v} value={v}>{v}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">📏 Length</label>
              <div className="ph-select-wrap">
                <select value={filters.length} onChange={e => upd("length", e.target.value)}><option value="">All Lengths</option>{lengths.map(l => <option key={l} value={l}>{l}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">👷 Buncher</label>
              <div className="ph-select-wrap">
                <select value={filters.worker} onChange={e => upd("worker", e.target.value)}><option value="">All Bunchers</option>{workers.map(w => <option key={w} value={w}>{w}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <div className="ph-filter-group">
              <label className="ph-filter-label">🔖 Harvest Type</label>
              <div className="ph-select-wrap">
                <select value={filters.harvest_type} onChange={e => upd("harvest_type", e.target.value)}><option value="">All Types</option>{harvestTypes.map(h => <option key={h} value={h}>{h}</option>)}</select>
                <span className="ph-select-arrow">▾</span>
              </div>
            </div>
            <button className="ph-reset-btn" onClick={resetFilters}>✕ Reset</button>
          </div>
          <div className="ph-quick-dates">
            {DATE_PRESETS.map(p => {
              const r = getPresetRange(p);
              const active = r.dateFrom === filters.dateFrom && r.dateTo === filters.dateTo;
              return <MiniChip key={p.label} label={p.label} active={active} onClick={() => setFilters(f => ({ ...f, dateFrom: r.dateFrom, dateTo: r.dateTo }))} />;
            })}
          </div>
        </div>
      )}

      {/* ── STATS ── */}
      <div className={`ph-stats-grid ${animateStats ? "ph-stats-animate" : ""}`}>
        <StatCard label="Total Stems Packed" value={fmt(totalStems)} icon="🌺" accent={BRAND.pink} sub={`${fmt(filtered.length)} captures`} trend={trends.stems} sparkData={timeSeries} sparkKey="stems" />
        <StatCard label="Total Bunches" value={fmt(totalBunches)} icon="💐" accent={BRAND.olive} sub="packed bunches" sparkData={timeSeries} sparkKey="bunches" />
        <StatCard label="Active Bunchers" value={fmt(uniqueWorkers)} icon="👷" accent="#4b8ca8" sub="unique contributors" />
        <StatCard label="Avg Bunch Size" value={avgBunchSize} icon="📐" accent="#6fa84b" sub="stems per bunch" />
      </div>

      {/* ── TABS ── */}
      <div className="ph-tabs-bar">
        <div className="ph-tabs">
          {TABS.map(t => (
            <button key={t.id} className={`ph-tab ${tab === t.id ? "ph-tab-active" : ""}`} onClick={() => setTab(t.id)}>
              <span className="ph-tab-icon">{t.icon}</span><span className="ph-tab-label">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="ph-content">

        {/* ════ OVERVIEW ════ */}
        {tab === "overview" && (
          <div className="ph-section">
            {/* Harvest Trend */}
            <div className="ph-chart-card ph-chart-wide">
              <div className="ph-chart-header">
                <h3>📈 Harvest Trend</h3>
                <div className="ph-chart-legend">
                  <span><div style={{background:BRAND.pink}}/>Stems</span>
                  <span><div style={{background:BRAND.olive}}/>Bunches</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={timeSeries}>
                  <defs><linearGradient id="stemsGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BRAND.pink} stopOpacity={0.2}/><stop offset="95%" stopColor={BRAND.pink} stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#9ca3af" fontSize={12} />
                  <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area yAxisId="left" type="monotone" dataKey="stems" stroke={BRAND.pink} fill="url(#stemsGrad)" strokeWidth={3} name="Stems" />
                  <Line yAxisId="right" type="monotone" dataKey="bunches" stroke={BRAND.olive} strokeWidth={3} dot={{ fill: BRAND.olive, r: 4 }} name="Bunches" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Hourly Productivity */}
            <div className="ph-chart-card ph-chart-wide">
              <div className="ph-chart-header"><h3>⏰ Hourly Productivity</h3></div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="stems" fill={BRAND.pink} radius={[6, 6, 0, 0]} name="Stems" />
                  <Bar dataKey="bunches" fill={BRAND.olive} radius={[6, 6, 0, 0]} name="Bunches" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Activity Heatmap */}
            <Heatmap data={filtered} />

            {/* Top Performers */}
            <div className="ph-two-col">
              <div className="ph-panel">
                <div className="ph-panel-hdr"><h3>🏆 Top Bunchers</h3><button className="ph-dl-btn" onClick={() => downloadCSV(byWorker.map(b => ({ name: b.name, stems: b.stems, bunches: b.bunches, captures: b.captures })), "top_bunchers.csv")}>⬇ CSV</button></div>
                {byWorker.slice(0, 6).map((b, i) => (
                  <div key={b.name} className="ph-hbar">
                    <div className="ph-hbar-meta"><span className="ph-hbar-rank">{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</span><span className="ph-hbar-label">{b.name}</span></div>
                    <div className="ph-hbar-track"><div className="ph-hbar-fill" style={{ width: `${byWorker[0]?.stems ? (b.stems / byWorker[0].stems) * 100 : 0}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                    <span className="ph-hbar-val">{fmt(b.stems)}</span>
                  </div>
                ))}
              </div>
              <div className="ph-panel">
                <div className="ph-panel-hdr"><h3>🏠 Top Greenhouses</h3><button className="ph-dl-btn" onClick={() => downloadCSV(byGHSE, "top_ghses.csv")}>⬇ CSV</button></div>
                {byGHSE.slice(0, 6).map((g, i) => (
                  <div key={g.name} className="ph-hbar">
                    <div className="ph-hbar-meta"><span className="ph-hbar-rank">{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</span><span className="ph-hbar-label">{g.name}</span></div>
                    <div className="ph-hbar-track"><div className="ph-hbar-fill" style={{ width: `${byGHSE[0]?.stems ? (g.stems / byGHSE[0].stems) * 100 : 0}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                    <span className="ph-hbar-val">{fmt(g.stems)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════ BUNCHERS ════ */}
        {tab === "bunchers" && (
          <div className="ph-section">
            <div className="ph-section-header"><h2>👷 Buncher Leaderboard</h2><button className="ph-dl-btn" onClick={() => downloadCSV(byWorker.map(b => ({ Name: b.name, Stems: b.stems, Bunches: b.bunches, Captures: b.captures, Varieties: b.varieties, GHSEs: b.workers, "Avg Stems/Capture": b.captures ? Math.round(b.stems / b.captures) : 0 })), "buncher_leaderboard.csv")}>⬇ Download CSV</button></div>
            {byWorker.length > 0 && <Podium data={byWorker} valueKey="stems" unit="stems" />}
            <div className="ph-table-wrap">
              <table className="ph-table ph-table-sortable">
                <thead><tr><th>Rank</th><th>Buncher</th><th className="ph-num">Stems</th><th className="ph-num">Bunches</th><th className="ph-num">Captures</th><th className="ph-num">Varieties</th><th className="ph-num">Avg/Capture</th><th>Share</th></tr></thead>
                <tbody>
                  {byWorker.map((b, i) => (
                    <tr key={b.name} className={i < 3 ? "ph-top-row" : ""}>
                      <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                      <td className="ph-name-cell">{b.name}</td>
                      <td className="ph-num ph-stems-num">{fmt(b.stems)}</td>
                      <td className="ph-num">{fmt(b.bunches)}</td>
                      <td className="ph-num">{b.captures}</td>
                      <td className="ph-num">{b.varieties}</td>
                      <td className="ph-num">{b.captures ? fmt(Math.round(b.stems / b.captures)) : "—"}</td>
                      <td><div className="ph-inline-bar"><div className="ph-inline-fill" style={{ width: `${pct(b.stems, totalStems)}%`, background: PALETTE[i % PALETTE.length] }} /><span>{pct(b.stems, totalStems)}%</span></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════ GREENHOUSE ════ */}
        {tab === "greenhouse" && (
          <div className="ph-section">
            <div className="ph-section-header"><h2>🏠 Greenhouse Performance</h2><button className="ph-dl-btn" onClick={() => downloadCSV(byGHSE, "by_ghse.csv")}>⬇ Download CSV</button></div>
            <div className="ph-farm-grid">
              {byFarm.map((f, i) => (
                <div key={f.name} className="ph-farm-card" style={{ "--fc": PALETTE[i % PALETTE.length] }}>
                  <div className="ph-farm-header"><span className="ph-farm-icon">🚜</span><span className="ph-farm-name">{f.name}</span></div>
                  <div className="ph-farm-big">{fmt(f.stems)}</div>
                  <div className="ph-farm-label">stems packed</div>
                  <div className="ph-farm-bar-wrap"><div className="ph-farm-bar-fill" style={{ width: `${pct(f.stems, totalStems)}%`, background: "var(--fc)" }} /></div>
                  <div className="ph-farm-meta"><span>{fmt(f.bunches)} bunches</span><span>{pct(f.stems, totalStems)}% of total</span></div>
                </div>
              ))}
            </div>
            <div className="ph-table-wrap">
              <table className="ph-table">
                <thead><tr><th>Rank</th><th>Greenhouse</th><th className="ph-num">Stems</th><th className="ph-num">Bunches</th><th className="ph-num">Bunchers</th><th className="ph-num">Captures</th><th>Share</th></tr></thead>
                <tbody>
                  {byGHSE.map((g, i) => (
                    <tr key={g.name} className={i < 3 ? "ph-top-row" : ""}>
                      <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                      <td className="ph-name-cell">{g.name}</td>
                      <td className="ph-num ph-stems-num">{fmt(g.stems)}</td>
                      <td className="ph-num">{fmt(g.bunches)}</td>
                      <td className="ph-num">{g.workers}</td>
                      <td className="ph-num">{g.captures}</td>
                      <td><div className="ph-inline-bar"><div className="ph-inline-fill" style={{ width: `${pct(g.stems, totalStems)}%`, background: PALETTE[i % PALETTE.length] }} /><span>{pct(g.stems, totalStems)}%</span></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════ LENGTHS ════ */}
        {tab === "lengths" && (
          <div className="ph-section">
            <div className="ph-section-header"><h2>📏 Stems Per Length</h2><button className="ph-dl-btn" onClick={() => downloadCSV(byLength, "by_length.csv")}>⬇ Download CSV</button></div>
            <div className="ph-length-cards">
              {byLength.map((l, i) => (
                <div key={l.name} className="ph-lcard" style={{ "--lc": PALETTE[i % PALETTE.length] }}>
                  <div className="ph-lcard-rank">{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</div>
                  <div className="ph-lcard-name">{l.name}</div>
                  <div className="ph-lcard-stems">{fmt(l.stems)}</div>
                  <div className="ph-lcard-unit">stems</div>
                  <div className="ph-lcard-sub">{fmt(l.bunches)} bunches · {l.captures} captures</div>
                  <div className="ph-lcard-bar"><div style={{ width: `${pct(l.stems, totalStems)}%` }} /></div>
                  <div className="ph-lcard-pct">{pct(l.stems, totalStems)}%</div>
                </div>
              ))}
            </div>
            <div className="ph-chart-card ph-chart-wide">
              <div className="ph-chart-header"><h3>📊 Length Distribution</h3></div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byLength} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="stems" fill={BRAND.pink} radius={[0, 6, 6, 0]} name="Stems" />
                  <Bar dataKey="bunches" fill={BRAND.olive} radius={[0, 6, 6, 0]} name="Bunches" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="ph-panel">
              <div className="ph-panel-hdr"><h3>📊 Length × Greenhouse Cross-Tab</h3><button className="ph-dl-btn" onClick={() => downloadCSV(crossLGHSE, "length_ghse_cross.csv")}>⬇ CSV</button></div>
              <div className="ph-table-wrap">
                <table className="ph-table">
                  <thead><tr><th>Length</th><th>Greenhouse</th><th className="ph-num">Stems</th><th className="ph-num">Bunches</th><th className="ph-num">Avg Bunch Size</th></tr></thead>
                  <tbody>
                    {crossLGHSE.map((r, i) => (
                      <tr key={i}>
                        <td><span className="ph-length-pill">{r.length}</span></td>
                        <td>{r.ghse}</td>
                        <td className="ph-num ph-stems-num">{fmt(r.stems)}</td>
                        <td className="ph-num">{fmt(r.bunches)}</td>
                        <td className="ph-num">{r.bunches ? Math.round(r.stems / r.bunches) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ════ VARIETIES ════ */}
        {tab === "varieties" && (
          <div className="ph-section">
            <div className="ph-section-header"><h2>🌿 Variety Performance</h2><button className="ph-dl-btn" onClick={() => downloadCSV(byVariety, "by_variety.csv")}>⬇ Download CSV</button></div>

            {/* Activity Heatmap */}
            <Heatmap data={filtered} />

            <div className="ph-two-col">
              <div className="ph-chart-card">
                <div className="ph-chart-header"><h3>🥧 Variety Share</h3></div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={varietyPie} cx="50%" cy="50%" outerRadius={100} paddingAngle={3} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {varietyPie.map((entry, index) => <Cell key={`cell-${index}`} fill={PALETTE[index % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="ph-chart-card">
                <div className="ph-chart-header"><h3>📊 Variety Treemap</h3></div>
                <ResponsiveContainer width="100%" height={300}>
                  <Treemap data={treemapData} dataKey="size" aspectRatio={4 / 3} stroke="#fff" fill={BRAND.pink} content={({ x, y, width, height, index, name, stems }) => (
                    <g>
                      <rect x={x} y={y} width={width} height={height} style={{ fill: PALETTE[index % PALETTE.length], stroke: "#fff", strokeWidth: 2 }} />
                      {width > 60 && height > 40 && <text x={x + 10} y={y + 20} fill="#fff" fontSize={12} fontWeight="bold">{name}</text>}
                      {width > 60 && height > 40 && <text x={x + 10} y={y + 36} fill="#fff" fontSize={11}>{fmt(stems)} stems</text>}
                    </g>
                  )} />
                </ResponsiveContainer>
              </div>
            </div>
            <div className="ph-table-wrap">
              <table className="ph-table">
                <thead><tr><th>Rank</th><th>Variety</th><th className="ph-num">Stems</th><th className="ph-num">Bunches</th><th className="ph-num">Captures</th><th className="ph-num">Avg Stems/Bunch</th><th>Share</th></tr></thead>
                <tbody>
                  {byVariety.map((v, i) => (
                    <tr key={v.name} className={i < 3 ? "ph-top-row" : ""}>
                      <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                      <td className="ph-name-cell">{v.name}</td>
                      <td className="ph-num ph-stems-num">{fmt(v.stems)}</td>
                      <td className="ph-num">{fmt(v.bunches)}</td>
                      <td className="ph-num">{v.captures}</td>
                      <td className="ph-num">{v.bunches ? Math.round(v.stems / v.bunches) : "—"}</td>
                      <td><div className="ph-inline-bar"><div className="ph-inline-fill" style={{ width: `${pct(v.stems, totalStems)}%`, background: PALETTE[i % PALETTE.length] }} /><span>{pct(v.stems, totalStems)}%</span></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════ HARVEST TYPE ════ */}
        {tab === "analytics" && (
          <div className="ph-section">
            <div className="ph-section-header">
              <h2>🔖 Harvest Type Breakdown</h2>
              <button className="ph-dl-btn" onClick={() => downloadCSV(byHarvestType, "by_harvest_type.csv")}>⬇ Download CSV</button>
            </div>

            {/* Summary cards */}
            <div className="ph-harvest-summary">
              {byHarvestType.map((h, i) => (
                <div key={h.name} className="ph-harvest-card" style={{ "--hc": PALETTE[i % PALETTE.length] }}>
                  <div className="ph-harvest-card-top">
                    <span className="ph-harvest-rank">{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</span>
                    <span className="ph-harvest-pct">{pct(h.stems, totalStems)}%</span>
                  </div>
                  <div className="ph-harvest-name">{h.name}</div>
                  <div className="ph-harvest-stems">{fmt(h.stems)}</div>
                  <div className="ph-harvest-unit">stems</div>
                  <div className="ph-harvest-sub">{fmt(h.bunches)} bunches · {h.captures} captures</div>
                  <div className="ph-harvest-bar"><div style={{ width: `${pct(h.stems, totalStems)}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                </div>
              ))}
            </div>

            {/* Bar chart */}
            <div className="ph-chart-card ph-chart-wide">
              <div className="ph-chart-header"><h3>📊 Stems & Bunches by Harvest Type</h3></div>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={byHarvestType} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} angle={-15} textAnchor="end" interval={0} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="stems" fill={BRAND.pink} radius={[6, 6, 0, 0]} name="Stems" />
                  <Bar dataKey="bunches" fill={BRAND.olive} radius={[6, 6, 0, 0]} name="Bunches" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Detailed table */}
            <div className="ph-table-wrap">
              <table className="ph-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Harvest Type</th>
                    <th className="ph-num">Stems</th>
                    <th className="ph-num">Bunches</th>
                    <th className="ph-num">Captures</th>
                    <th className="ph-num">Avg Stems/Bunch</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {byHarvestType.map((h, i) => (
                    <tr key={h.name} className={i < 3 ? "ph-top-row" : ""}>
                      <td>{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</td>
                      <td className="ph-name-cell">{h.name}</td>
                      <td className="ph-num ph-stems-num">{fmt(h.stems)}</td>
                      <td className="ph-num">{fmt(h.bunches)}</td>
                      <td className="ph-num">{h.captures}</td>
                      <td className="ph-num">{h.bunches ? Math.round(h.stems / h.bunches) : "—"}</td>
                      <td>
                        <div className="ph-inline-bar">
                          <div className="ph-inline-fill" style={{ width: `${pct(h.stems, totalStems)}%`, background: PALETTE[i % PALETTE.length] }} />
                          <span>{pct(h.stems, totalStems)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════ RAW DATA ════ */}
        {tab === "table" && (
          <div className="ph-section">
            <div className="ph-section-header"><h2>📋 Capture Records <span className="ph-count-badge">{fmt(filtered.length)}</span></h2><button className="ph-dl-btn" onClick={() => downloadCSV(sortedFiltered.map(c => ({ ...c, total_stems: calcStems(c) })), "all_captures.csv")}>⬇ Export All</button></div>
            {filtered.length === 0 ? (
              <div className="ph-empty"><div className="ph-empty-icon">💐</div><div className="ph-empty-msg">No data for the selected filters.</div></div>
            ) : (
              <>
                <div className="ph-table-wrap">
                  <table className="ph-table ph-table-sortable">
                    <thead>
                      <tr>
                        {[{ col: "captured_at", label: "Date / Time" }, { col: "worker_name", label: "Buncher" }, { col: "farm_name", label: "Farm" }, { col: "ghse_name", label: "GHSE" }, { col: "variety_name", label: "Variety" }, { col: "length_name", label: "Length" }, { col: "harvest_type_name", label: "Type" }, { col: "bunch_size_value", label: "Bunch Size" }, { col: "bunch_count", label: "Bunches" }, { col: "total_stems", label: "Stems" }].map(({ col, label }) => (
                          <th key={col} onClick={() => handleSort(col)} className="ph-sortable">{label}{sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ↕"}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((c, i) => (
                        <tr key={c.id || i}>
                          <td className="ph-date-cell"><div>{fmtDate(c.captured_at)}</div><div className="ph-time">{fmtTime(c.captured_at)}</div></td>
                          <td className="ph-name-cell">{c.worker_name || c.worker || "—"}</td>
                          <td><span className="ph-farm-pill">{farmName(c) || "—"}</span></td>
                          <td>{c.ghse_name || "—"}</td>
                          <td>{c.variety_name || "—"}</td>
                          <td><span className="ph-length-pill">{c.length_name || "—"}</span></td>
                          <td><span className="ph-type-pill">{c.harvest_type_name || "—"}</span></td>
                          <td className="ph-num">{parseInt(c.bunch_size_value) || "—"}</td>
                          <td className="ph-num">{fmt(c.bunch_count)}</td>
                          <td className="ph-num ph-stems-num">{fmt(calcStems(c))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="ph-pagination">
                    <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                    <span className="ph-page-info">Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>

      {/* ── FOOTER ── */}
      <footer className="ph-footer">
        <span>Packhouse Performance · {new Date().getFullYear()}</span>
        <span>{fmt(filtered.length)} captures · {fmt(totalStems)} stems · {fmt(totalBunches)} bunches</span>
      </footer>
    </div>
  );
};

export default PackhouseDashboard;